import React, { useEffect, useState, useCallback, useRef } from "react";
import { NotificationManager } from "react-notifications";
import {
  BrowserMultiFormatReader,
  BarcodeFormat,
  DecodeHintType,
} from "@zxing/library";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import {
  QrCodeScanner as ScannerIcon,
  Videocam as VideoIcon,
  VideocamOff as VideocamOffIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  CameraAlt as CameraIcon,
} from "@mui/icons-material";

// Shared across scans and remounts; created lazily on the first beep.
let sharedAudioContext = null;

function getAudioContext() {
  if (!sharedAudioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    sharedAudioContext = new AudioContextClass();
  }
  return sharedAudioContext;
}

// Lower camera resolution and scan rate keep memory low on tablets, where
// full-resolution frames every 0.5s caused Safari to kill the page.
const CAMERA_RESOLUTION = { width: { ideal: 640 }, height: { ideal: 480 } };
const SCAN_INTERVAL_MS = 1000;
const SCAN_FORMATS = [BarcodeFormat.CODE_128, BarcodeFormat.QR_CODE];

export default function ScannerNew({ onScan }) {
  const [deviceId, setDeviceId] = useState();
  const [devices, setDevices] = useState();
  const [stopStream, setStopStream] = useState(false);
  const videoRef = useRef(null);
  // Latest onScan, so the running scan loop never calls a stale callback.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const handleDevices = useCallback(
    (mediaDevices) =>
      setDevices(mediaDevices.filter(({ kind }) => kind === "videoinput")),
    [setDevices]
  );

  const handleDeviceIdChange = (e) => {
    setDeviceId(e.target.value);
  };

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const beep = (frequency, durationSec) => {
    // Reuse one AudioContext for every beep. iOS Safari allows only a few
    // per page, and creating one per scan leaked memory on tablets.
    const audioContext = getAudioContext();
    if (!audioContext) return;
    if (audioContext.state === "suspended") audioContext.resume();

    const startTime = audioContext.currentTime;
    const oscillatorNode = audioContext.createOscillator();
    oscillatorNode.type = "sine";
    oscillatorNode.frequency.setValueAtTime(frequency, startTime);
    oscillatorNode.connect(audioContext.destination);
    oscillatorNode.onended = () => oscillatorNode.disconnect();
    oscillatorNode.start(startTime);
    oscillatorNode.stop(startTime + durationSec);
  };

  // One reader per camera session. It reuses a single canvas and is reset on
  // stop/unmount, which releases the camera and canvas. The old library made a
  // new reader, image and canvas every tick, and iOS Safari never freed them.
  useEffect(() => {
    if (stopStream || !videoRef.current) return;

    const hints = new Map([[DecodeHintType.POSSIBLE_FORMATS, SCAN_FORMATS]]);
    const reader = new BrowserMultiFormatReader(hints, SCAN_INTERVAL_MS);
    let cancelled = false;

    const constraints = {
      audio: false,
      video: deviceId
        ? { deviceId: deviceId, ...CAMERA_RESOLUTION }
        : { facingMode: "environment", ...CAMERA_RESOLUTION },
    };

    reader
      .decodeFromConstraints(constraints, videoRef.current, (result) => {
        // Frames without a barcode come back with no result; ignore them.
        if (!result || cancelled) return;
        beep(550, 0.2);
        onScanRef.current(result.getText());
      })
      .then(() => {
        // Camera opened after we were stopped/unmounted: release it now.
        if (cancelled) reader.reset();
      })
      .catch((err) => {
        if (!cancelled) {
          NotificationManager.error(err.toString(), "Error Starting Scan");
        }
      });

    return () => {
      cancelled = true;
      reader.reset();
    };
  }, [deviceId, stopStream]);

  return (
    <Card
      elevation={0}
      sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Scanner Header */}
        <Box
          sx={{
            background: "linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)",
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  borderRadius: 1.5,
                  p: 0.75,
                  mr: 1.5,
                  display: "flex",
                }}
              >
                <ScannerIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                Barcode Scanner
              </Typography>
            </Box>

            <Chip
              label={stopStream ? "Stopped" : "Active"}
              size="small"
              color={stopStream ? "default" : "success"}
              icon={stopStream ? <VideocamOffIcon /> : <VideoIcon />}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Scanner Display Area */}
          {!stopStream ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  position: "relative",
                  mb: 2,
                }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    border: 3,
                    borderColor: "primary.main",
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        linear-gradient(90deg, transparent 0%, transparent 45%, rgba(76, 175, 80, 0.3) 50%, transparent 55%, transparent 100%),
                        linear-gradient(0deg, transparent 0%, transparent 45%, rgba(76, 175, 80, 0.3) 50%, transparent 55%, transparent 100%)
                      `,
                      pointerEvents: "none",
                      zIndex: 1,
                    },
                  }}
                >
                  <video
                    ref={videoRef}
                    width={500}
                    height={500}
                    muted
                    playsInline
                  />
                </Paper>
              </Box>

              {/* Scanner Instructions */}
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "primary.50",
                  borderRadius: 2,
                  border: 1,
                  borderColor: "primary.200",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight="500"
                  gutterBottom
                >
                  📱 Scanning Instructions:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Position the barcode or QR code within the camera frame •
                  Ensure good lighting and steady hands for best results •
                  Compatible with CODE_128 and QR code formats • You'll hear a
                  beep when a code is successfully scanned
                </Typography>
              </Box>
            </Box>
          ) : null}

          {/* Controls */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              {devices ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Camera Device</InputLabel>
                  <Select
                    value={deviceId || ""}
                    onChange={handleDeviceIdChange}
                    label="Camera Device"
                    startAdornment={
                      <CameraIcon sx={{ mr: 1, color: "action.active" }} />
                    }
                  >
                    {devices.map((device, key) => (
                      <MenuItem key={key} value={device.deviceId}>
                        {device.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="medium"
                startIcon={stopStream ? <PlayIcon /> : <StopIcon />}
                onClick={() => setStopStream(!stopStream)}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Toggle Scan
              </Button>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
}